import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, ArrowRight, Shield, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGlobalConnect } from "@/hooks/useGlobalConnect";
import { useGlobalIntents, GlobalIntent } from "@/hooks/useGlobalIntents";
import { useAuthContext } from "@/contexts/AuthContext";
import { GlobalIntentGateModal } from "@/components/global-connect/GlobalIntentGateModal";
import { GlobalFilterDrawer } from "@/components/global-connect/GlobalFilterDrawer";
import { GlobalFilterChip } from "@/components/global-connect/GlobalFilterChip";
import { GlobalConnectCountryExplorer } from "@/components/global-connect/GlobalConnectCountryExplorer";
import { GlobalConnectLimitsBar } from "@/components/global-connect/GlobalConnectLimitsBar";
import ErrorBoundary from "@/components/ErrorBoundary";

const GlobalConnect = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuthContext();
  const { settings, limits, loading: gcLoading, isEnabled, enableGlobalConnect, refetchLimits } = useGlobalConnect();
  const {
    selectedIntents,
    setSelectedIntents,
    filterPreferences,
    setFilterPreferences,
    loading: intentsLoading,
    hasIntents,
    saveIntents,
    saveFilterPreferences,
  } = useGlobalIntents();

  const [showIntentGate, setShowIntentGate] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [showChangeIntent, setShowChangeIntent] = useState(false);
  const [hasPassedGate, setHasPassedGate] = useState(false);

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  // Show intent gate if no intents set and not loading
  useEffect(() => {
    if (!intentsLoading && !authLoading && user && !hasIntents && !hasPassedGate) {
      setShowIntentGate(true);
    }
  }, [intentsLoading, authLoading, user, hasIntents, hasPassedGate]);

  const loading = authLoading || gcLoading || intentsLoading;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // Handle intent gate confirmation
  const handleIntentGateConfirm = async (intents: GlobalIntent[]) => {
    await saveIntents(intents);
    setShowIntentGate(false);
    setHasPassedGate(true);

    // Also enable Global Connect if not already enabled
    if (!isEnabled && intents.length > 0) {
      // Map first intent to old format for enableGlobalConnect
      const legacyIntent = intents[0].replace("global_", "") as "curious" | "dating" | "serious";
      await enableGlobalConnect(legacyIntent);
      await refetchLimits();
    }
  };

  // Handle change intent (from within Global Connect)
  const handleChangeIntentConfirm = async (intents: GlobalIntent[]) => {
    await saveIntents(intents);
    setShowChangeIntent(false);
  };

  // Handle filter apply
  const handleFilterApply = async () => {
    await saveFilterPreferences(filterPreferences);
  };

  // Check if there are active filters beyond intents
  const hasActiveFilters =
    !!filterPreferences.countryCode ||
    !!filterPreferences.cityId ||
    filterPreferences.nearbyEnabled ||
    filterPreferences.verifiedOnly ||
    filterPreferences.onlineOnly ||
    (filterPreferences.ageMin && filterPreferences.ageMin > 18) ||
    (filterPreferences.ageMax && filterPreferences.ageMax < 65);

  // Show main content if has intents or passed gate
  const showMainContent = hasIntents || hasPassedGate;

  // Show country explorer if has intents and enabled (or just passed gate)
  if (showMainContent) {
    return (
      <div className="min-h-screen bg-background pb-20">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Globe className="w-6 h-6 text-primary" />
                  <h1 className="text-xl font-bold">Global Connect</h1>
                </div>
                {/* Filter Chip */}
                <GlobalFilterChip
                  selectedIntents={selectedIntents}
                  hasActiveFilters={hasActiveFilters}
                  onClick={() => setShowFilterDrawer(true)}
                />
              </div>
              {/* Change Intent Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowChangeIntent(true)}
                className="text-sm"
              >
                <Settings2 className="w-4 h-4 mr-1" />
                Intent
              </Button>
            </div>
            {limits && <GlobalConnectLimitsBar limits={limits} />}
          </div>
        </div>

        {/* Safety Banner */}
        <div className="mx-4 mt-4 p-3 bg-primary/5 rounded-lg border border-primary/10">
          <div className="flex items-start gap-2">
            <Shield className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <p className="text-xs text-muted-foreground">
              Global Connect is for virtual, respectful connections. No travel pressure or financial requests.
            </p>
          </div>
        </div>

        {/* Country Explorer - Wrapped in error boundary */}
        <ErrorBoundary
          fallback={
            <div className="p-4 text-center">
              <p className="text-muted-foreground mb-4">
                Unable to load country explorer. Please try refreshing.
              </p>
              <Button onClick={() => window.location.reload()}>Refresh Page</Button>
            </div>
          }
        >
          <GlobalConnectCountryExplorer />
        </ErrorBoundary>

        {/* Filter Drawer */}
        <GlobalFilterDrawer
          isOpen={showFilterDrawer}
          onClose={() => setShowFilterDrawer(false)}
          selectedIntents={selectedIntents}
          onIntentsChange={setSelectedIntents}
          filterPreferences={filterPreferences}
          onFilterChange={setFilterPreferences}
          onApply={handleFilterApply}
        />

        {/* Change Intent Modal */}
        <GlobalIntentGateModal
          isOpen={showChangeIntent}
          onClose={() => setShowChangeIntent(false)}
          onConfirm={handleChangeIntentConfirm}
          initialIntents={selectedIntents}
          isUpdate={true}
        />
      </div>
    );
  }

  // Entry screen for new users (shows when gate is closed but no intents)
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex flex-col pb-20">
      {/* Hero Section */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
        {/* Globe Illustration */}
        <div className="relative mb-8">
          <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
            <Globe className="w-16 h-16 text-primary" />
          </div>
          <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary/20 animate-pulse" />
          <div className="absolute -bottom-1 -left-3 w-6 h-6 rounded-full bg-accent/30 animate-pulse delay-300" />
        </div>

        <h1 className="text-3xl font-bold mb-3">Global Connect</h1>
        <p className="text-lg text-primary font-medium mb-2">
          Meet people across cultures
        </p>
        <p className="text-muted-foreground max-w-xs mb-8">
          Connect globally. Virtually. Respectfully.
        </p>

        {/* Features */}
        <div className="grid grid-cols-1 gap-3 w-full max-w-xs mb-8">
          <FeatureItem
            icon="🌍"
            title="Cultural Exchange"
            description="Learn about different cultures"
          />
          <FeatureItem
            icon="💬"
            title="Virtual First"
            description="No pressure for travel or relocation"
          />
          <FeatureItem
            icon="🛡️"
            title="Safe & Respectful"
            description="Strict guidelines against exploitation"
          />
        </div>

        {/* CTA Button */}
        <Button
          size="lg"
          className="w-full max-w-xs gap-2"
          onClick={() => setShowIntentGate(true)}
        >
          Get Started
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Intent Gate Modal */}
      <GlobalIntentGateModal
        isOpen={showIntentGate}
        onClose={() => {
          // Only allow close if has intents
          if (hasIntents) {
            setShowIntentGate(false);
          }
        }}
        onConfirm={handleIntentGateConfirm}
        initialIntents={selectedIntents}
        isUpdate={false}
      />
    </div>
  );
};

interface FeatureItemProps {
  icon: string;
  title: string;
  description: string;
}

const FeatureItem = ({ icon, title, description }: FeatureItemProps) => (
  <div className="flex items-center gap-3 p-3 bg-card/50 rounded-lg border border-border/50">
    <span className="text-2xl">{icon}</span>
    <div className="text-left">
      <p className="font-medium text-sm">{title}</p>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  </div>
);

export default GlobalConnect;
